
import { defineComponent, inject } from 'vue'
import CloseSvg from '@/components/svgs/CloseSvg.vue'
import TextInput from '@/components/shared/TextInput.vue'
import { Contact } from '@/@types/responses/contact/Contact'
import { PROVIDER_CONSTANTS } from '@/constants/provider.constants'
import Toaster from '@meforma/vue-toaster'
import PhoneNumberInput from '@/components/shared/PhoneNumberInput.vue'
import { ContactMessage } from '@/@types/messages/ContactMessage'
import { ManufacturerService } from '@/services/manufacturer.service'
import { SupplierService } from '@/services/supplier.service'
import { ContactService } from '@/services/contact.service'
import LockingButton from '@/components/shared/LockingButton.vue'
import { ErrorsResponse } from '@/@types/responses/common/ErrorsResponse'

export default defineComponent({
  name: 'ContactModal',
  components: {
    CloseSvg,
    LockingButton,
    PhoneNumberInput,
    TextInput
  },
  props: {
    contact: {
      type: Contact,
      required: true
    },
    manufacturerId: {
      type: Number,
      default: undefined
    },
    supplierId: {
      type: Number,
      default: undefined
    }
  },
  emits: ['contact-changed', 'close'],
  setup() {
    const contactService = inject(PROVIDER_CONSTANTS.contactService) as ContactService
    const manufacturerService = inject(PROVIDER_CONSTANTS.manufacturerService) as ManufacturerService
    const supplierService = inject(PROVIDER_CONSTANTS.supplierService) as SupplierService
    const toast = inject(PROVIDER_CONSTANTS.toaster) as Toaster

    return {
      contactService,
      manufacturerService,
      supplierService,
      toast
    }
  },
  data() {
    return {
      firstName: this.contact.firstName,
      lastName: this.contact.lastName,
      phoneNumber: this.contact.phoneNumber,
      email: this.contact.email,
      saving: false
    }
  },
  computed: {
    isFormValid(): boolean {
      return (this.firstName !== this.contact.firstName ||
             this.lastName !== this.contact.lastName ||
             this.phoneNumber !== this.contact.phoneNumber ||
             this.email !== this.contact.email) && (this.firstName.trim() !== '' && this.lastName.trim() !== '')
    },
    titleText(): string {
      return this.contact.id === 0 ? 'Create Contact' : 'Edit Contact'
    }
  },
  mounted() {
    document.body.addEventListener('keyup', this.onKeyup)
  },
  unmounted() {
    document.body.removeEventListener('keyup', this.onKeyup)
  },
  methods: {
    onCloseClicked(): void {
      this.$emit('close')
    },
    onKeyup(e: KeyboardEvent): void {
      if (e.key === 'Escape' || e.key === 'Esc')
        this.onCloseClicked()
    },
    onSaveClicked(): void {
      this.saving = true

      const contactMessage = new ContactMessage(this.firstName, this.lastName, this.phoneNumber, this.email)
      if (this.contact.id === 0) {
        if (this.manufacturerId !== undefined)
          this.manufacturerService.createManufacturerContact(this.manufacturerId, contactMessage).then(() => {
            this.$emit('contact-changed')
            this.toast.success('Contact created successfully.')
            this.$emit('close')
          }).catch(error => {
            this.saving = false

            if (error instanceof ErrorsResponse) {
              const errorsResponse = error as ErrorsResponse

              if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

              this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
            } else {
              this.toast.error('Failed to create the contact. Please try again.')
            }
          })
        else if (this.supplierId !== undefined)
          this.supplierService.createSupplierContact(this.supplierId, contactMessage).then(() => {
            this.$emit('contact-changed')
            this.toast.success('Contact created successfully.')
            this.$emit('close')
          }).catch(error => {
            this.saving = false

            if (error instanceof ErrorsResponse) {
              const errorsResponse = error as ErrorsResponse

              if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

              this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
            } else {
              this.toast.error('Failed to create the contact. Please try again.')
            }
          })
      } else
        this.contactService.updateContact(this.contact.id, contactMessage).then(() => {
          this.$emit('contact-changed')
          this.toast.success('Contact updated successfully.')
          this.$emit('close')
        }).catch(error => {
          this.saving = false

          if (error instanceof ErrorsResponse) {
            const errorsResponse = error as ErrorsResponse

            if (errorsResponse.errors.some(e => e.id === 33001 || e.id === 33002 || e.id === 33003)) return

            this.toast.error(errorsResponse.errors.map(e => e.message).join("\r\n"))
          } else {
            this.toast.error('Failed to updated the contact. Please try again.')
          }
        })
    }
  }
})
